import { createLucideIcon } from 'lucide-react';
import { primitives, tokens } from '@/styles/tokens';

/**
 * Audit tickmark glyph icons (M / Rx / SAD / PY, and the stroked GL at the end).
 * Shared by the Editor annotation tools and the Data-analysis marker picker so both
 * surfaces render the exact Figma glyphs. `color` fills the paths (defaults to the
 * brand teal).
 */
interface TickmarkIconProps {
  color?: string;
  width?: number;
  height?: number;
}

export const MIcon = ({
  color = tokens.primary.main,
  width = 16,
  height = 16,
}: TickmarkIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      fill={color}
      d='M4.385 12V4.14286H5.735V5.14286H6.035L5.81 5.44286C5.81 5.00476 5.935 4.65714 6.185 4.4C6.435 4.13333 6.77 4 7.19 4C7.64 4 7.985 4.16667 8.225 4.5C8.475 4.83333 8.6 5.29048 8.6 5.87143L8.24 5.14286H8.81L8.57 5.44286C8.57 5.00476 8.695 4.65714 8.945 4.4C9.205 4.13333 9.55 4 9.98 4C10.49 4 10.89 4.18571 11.18 4.55714C11.47 4.91905 11.615 5.40476 11.615 6.01429V12H10.175V6.05714C10.175 5.7619 10.105 5.53333 9.965 5.37143C9.835 5.20952 9.645 5.12857 9.395 5.12857C9.145 5.12857 8.95 5.20952 8.81 5.37143C8.68 5.52381 8.615 5.74762 8.615 6.04286V12H7.4V6.05714C7.4 5.75238 7.33 5.52381 7.19 5.37143C7.05 5.20952 6.85 5.12857 6.59 5.12857C6.34 5.12857 6.15 5.20952 6.02 5.37143C5.9 5.52381 5.84 5.74762 5.84 6.04286V12H4.385Z'
    />
    <path fill={color} d='M3.5 7.42857H12.5V8.14286H3.5V7.42857Z' />
  </svg>
);

export const RxIcon = ({
  color = tokens.primary.main,
  width = 16,
  height = 16,
}: TickmarkIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      fill={color}
      d='M2 12V4H4.39459C4.89908 4 5.33446 4.09863 5.70073 4.29589C6.07391 4.49315 6.36417 4.77078 6.57149 5.12877C6.77881 5.47945 6.88248 5.89589 6.88248 6.37808C6.88248 6.90411 6.75463 7.36804 6.49893 7.76986C6.25014 8.17169 5.91151 8.45662 5.48304 8.62466L6.98614 12H5.53487L4.21836 8.82192H3.29577V12H2ZM3.29577 7.61644H4.39459C4.76086 7.61644 5.04766 7.5105 5.25498 7.29863C5.46231 7.08676 5.56597 6.79452 5.56597 6.42192C5.56597 6.04201 5.46231 5.74612 5.25498 5.53425C5.04766 5.31507 4.76086 5.20548 4.39459 5.20548H3.29577V7.61644Z'
    />
    <path
      fill={color}
      d='M8.52665 12L10.517 8.88767L8.66141 5.9726H10.123L11.0042 7.45205C11.0595 7.53973 11.1078 7.63836 11.1493 7.74795C11.1977 7.85753 11.2357 7.94521 11.2633 8.01096C11.291 7.94521 11.329 7.85753 11.3774 7.74795C11.4257 7.63836 11.4741 7.53973 11.5225 7.45205L12.414 5.9726H13.8756L12.0201 8.88767L14 12H12.528L11.5432 10.3452C11.4879 10.2502 11.4361 10.1479 11.3877 10.0384C11.3393 9.92877 11.2979 9.8411 11.2633 9.77534C11.2357 9.8411 11.1977 9.92877 11.1493 10.0384C11.1009 10.1479 11.0491 10.2502 10.9938 10.3452L9.98828 12H8.52665Z'
    />
  </svg>
);

export const SadIcon = ({
  color = primitives.orange[100],
  width = 16,
  height = 16,
}: TickmarkIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      fill={color}
      d='M3.06973 11C2.64734 11 2.28126 10.9333 1.97151 10.8C1.66175 10.6667 1.42239 10.4773 1.25344 10.232C1.09011 9.98133 1.00563 9.688 1 9.352H2.05599C2.05599 9.592 2.1461 9.78133 2.32632 9.92C2.51217 10.0533 2.76279 10.12 3.07818 10.12C3.3823 10.12 3.61885 10.0533 3.7878 9.92C3.96239 9.78667 4.04969 9.60267 4.04969 9.368C4.04969 9.17067 3.98774 9 3.86383 8.856C3.74556 8.70667 3.57379 8.60533 3.34851 8.552L2.63889 8.368C2.15454 8.25067 1.78002 8.04 1.51532 7.736C1.25625 7.432 1.12672 7.064 1.12672 6.632C1.12672 6.30133 1.20557 6.01333 1.36326 5.768C1.52095 5.52267 1.74341 5.33333 2.03064 5.2C2.3235 5.06667 2.66705 5 3.06129 5C3.65827 5 4.13135 5.14667 4.48053 5.44C4.82971 5.728 5.00711 6.11733 5.01275 6.608H3.95676C3.95676 6.37867 3.87791 6.2 3.72022 6.072C3.56253 5.93867 3.34007 5.872 3.05284 5.872C2.77687 5.872 2.56286 5.93333 2.4108 6.056C2.25874 6.17867 2.1827 6.352 2.1827 6.576C2.1827 6.77867 2.23902 6.952 2.35166 7.096C2.46993 7.23467 2.63889 7.33333 2.85854 7.392L3.5935 7.584C4.08348 7.70133 4.458 7.912 4.71707 8.216C4.97614 8.51467 5.10567 8.88533 5.10567 9.328C5.10567 9.65867 5.02119 9.952 4.85224 10.208C4.68328 10.4587 4.44674 10.6533 4.14261 10.792C3.83849 10.9307 3.48086 11 3.06973 11Z'
    />
    <path
      fill={color}
      d='M5.7951 10.92L7.34951 5.08H8.70962L10.2725 10.92H9.19115L8.85324 9.504H7.21435L6.87643 10.92H5.7951ZM7.40865 8.688H8.65894L8.28723 7.12C8.22528 6.85867 8.17177 6.624 8.12672 6.416C8.08166 6.20267 8.05069 6.048 8.03379 5.952C8.0169 6.048 7.98592 6.20267 7.94086 6.416C7.90144 6.624 7.84794 6.856 7.78035 7.112L7.40865 8.688Z'
    />
    <path
      fill={color}
      d='M11.114 10.92V5.08H12.9472C13.3639 5.08 13.7244 5.15467 14.0285 5.304C14.3382 5.45333 14.5776 5.664 14.7466 5.936C14.9155 6.208 15 6.53067 15 6.904V9.088C15 9.456 14.9155 9.77867 14.7466 10.056C14.5776 10.328 14.3382 10.5413 14.0285 10.696C13.7244 10.8453 13.3639 10.92 12.9472 10.92H11.114ZM12.17 10H12.9472C13.2513 10 13.4935 9.92 13.6737 9.76C13.8539 9.59467 13.944 9.37067 13.944 9.088V6.904C13.944 6.62667 13.8539 6.408 13.6737 6.248C13.4935 6.08267 13.2513 6 12.9472 6H12.17V10Z'
    />
  </svg>
);

/** PY, the "audit tickmarks" set's PY variant (Figma 36306:30068). */
export const PyIcon = ({
  color = tokens.primary.main,
  width = 16,
  height = 16,
}: TickmarkIconProps) => (
  <svg
    width={width}
    height={height}
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      fill={color}
      d='M2 11V5H5.06364C5.39613 5 5.70147 5.05634 5.97968 5.16901C6.25788 5.28169 6.49876 5.4338 6.70233 5.62535C6.91268 5.8169 7.07553 6.03099 7.19088 6.26761C7.30623 6.50423 7.36391 6.74648 7.36391 6.99437C7.36391 7.33239 7.26891 7.65352 7.07892 7.95775C6.88893 8.26197 6.62769 8.50986 6.2952 8.70141C5.96271 8.89296 5.56916 8.98873 5.11453 8.98873H3.40459V11H2ZM3.40459 7.9662H5.0331C5.20274 7.9662 5.35541 7.92676 5.49112 7.84789C5.62683 7.76901 5.7354 7.65634 5.81682 7.50986C5.89825 7.35775 5.93896 7.18592 5.93896 6.99437C5.93896 6.78592 5.89146 6.60845 5.79647 6.46197C5.70826 6.31549 5.58951 6.20563 5.44023 6.13239C5.29774 6.05915 5.14167 6.02253 4.97203 6.02253H3.40459V7.9662Z'
    />
    <path
      fill={color}
      d='M8.89001 5L10.6712 7.82254L12.4829 5H13.9995L11.3735 8.90423V11H9.97908V8.88732L7.36328 5H8.89001Z'
    />
  </svg>
);

/**
 * GL is the ✓ with a short horizontal bar across the upper part of its longer arm
 * (working-paper annotations §4.3). Built on lucide's `check` path, so it takes the
 * props of the `Check` that draws ✓ and renders with the same stroke.
 */
export const GlIcon = createLucideIcon('tickmark-gl', [
  ['path', { d: 'M20 6 9 17l-5-5', key: 'check' }],
  ['path', { d: 'M14 9h6', key: 'bar' }],
]);
